"use client";
import { FlexBetween, FlexBetweenCol, FlexCol } from "@/components";
import { cn } from "@/lib";
import CustomButton from "../ui/customButton";
import { useForm } from "react-hook-form";
import { IInquiriesComponentProps, TIbanCard } from "@/interfaces";
import { zodResolver } from "@hookform/resolvers/zod";
import { ibanCardSchema } from "@/validations";
import CreditCard from "./creditCard";
import { useIbansInquiryMutation } from "@/hooks/useInquiry";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

import { useTranslations } from "next-intl";
import { routes } from "@/routes";
import { useModalStore } from "@/store";
import { landingQuickAccessNavigation } from "@/constants";
import { globalRawServicesData } from "@/constants/globalRawServicesData/globalRawServicesData";
import PaymentModalContainer from "@/components/Payment/paymentModalContainer";
import { globalRawServicesApiUnification } from "@/constants/globalRawServicesData/globalRawServicesApiUnification";
import { IIbansInquiryMutationPayload } from "@/interfaces/inquiries/bank";

const IbanInquiry = ({
  openPaymentModal,
  isLoggedIn,
  navigateToLogin,
  isOpen,
  onClose,
  packages,
}: IInquiriesComponentProps) => {
  const t = useTranslations();
  const { closeModal } = useModalStore();
  const { data: session } = useSession();
  const {
    isPending: isSubmitting,
    mutate: ibansInquiryFn,
    error: ibansInquiryError,
    reset: resetIbansInquiry,
  } = useIbansInquiryMutation(session?.user.token);
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors: formError },
  } = useForm<TIbanCard>({
    resolver: zodResolver(ibanCardSchema),
    defaultValues: {
      input1: "",
      input2: "",
      input3: "",
      input4: "",
      input5: "",
      input6: "",
    },
  });

  const router = useRouter();

  const onSubmit = (data: TIbanCard) => {
    if (!isLoggedIn) {
      navigateToLogin();
      return;
    }
    openPaymentModal();
  };

  useEffect(() => {
    if (isOpen) {
      resetIbansInquiry();
    }
  }, [isOpen, resetIbansInquiry]);

  const ibanInquiryDetails = landingQuickAccessNavigation.find(
    (item) => item.key === globalRawServicesData.Iban_Inquiry.key,
  );
  const [packageId, setPackageId] = useState<string | null>(null);
  const handlePackageSelect = (packageId: string) => {
    setPackageId(packageId);
  };
  const paymentSubmission = () => {
    const formData = getValues();
    const fullCard = Object.values(formData).join("");
    const payload: Readonly<IIbansInquiryMutationPayload> = {
      value: `IR${fullCard}`,
      campaign: packageId || undefined,
    };
    ibansInquiryFn(
      { payload },
      {
        onSuccess: (data) => {
          const purchase_id = data.data?.purchase_id;
          if (purchase_id) {
            router.push(
              `${routes.main.INQUIRY_DETAILS.primaryPath}/${data.data?.purchase_id}`,
            );
            closeModal();
          }
        },
      },
    );
  };

  return (
    <>
      <FlexBetween className="flex-col items-stretch gap-8 md:flex-row lg:gap-12">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={cn("flex-center mx-auto sm:basis-1/2")}
          style={{ maxWidth: "480px" }}
        >
          <FlexBetweenCol gap={4} className={cn("w-full xl:gap-8")}>
            <div>
              <CreditCard
                variant="iban"
                formError={formError}
                control={control}
                setValue={setValue}
              />
              {Object.keys(formError).length > 0 && (
                <p className="text-error-main mt-4 w-full text-xs">
                  {t("inquiries.ibanInquiry.cardError")}
                </p>
              )}
            </div>
            <CustomButton
              id="landing-iban-inquiry-submit"
              type="submit"
              className="mt-2 w-max lg:mt-4"
              onClick={handleSubmit(onSubmit)}
            >
              {t("inquiries.ibanInquiry.submitButton")}
            </CustomButton>
          </FlexBetweenCol>
        </form>

        <FlexCol
          gap={2}
          className={cn(
            "hidden basis-4/12 sm:flex lg:basis-5/12 lg:gap-4 xl:gap-6",
          )}
        >
          <h2 className={cn("text-sm font-bold lg:text-base xl:text-xl")}>
            {t("inquiries.ibanInquiry.headerTitle")}
          </h2>
          <p
            className={cn(
              "text-custom-gray-200 text-justify text-xs leading-5 whitespace-pre-line lg:text-sm lg:leading-normal xl:text-base",
            )}
          >
            {t("inquiries.ibanInquiry.description")}
          </p>
        </FlexCol>
      </FlexBetween>

      {isOpen && (
        <PaymentModalContainer
          payBtnId="landing-payment-iban-inquiry"
          submissionLoading={isSubmitting}
          submissionError={ibansInquiryError}
          paymentSubmission={paymentSubmission}
          isOpen={isOpen}
          onClose={onClose}
          showData="service"
          serviceId={globalRawServicesApiUnification.Iban_Inquiry.apiId}
          selectedService={ibanInquiryDetails}
          onErrorReset={resetIbansInquiry}
          packages={packages}
          onPackageSelect={handlePackageSelect}
        />
      )}
    </>
  );
};

export default IbanInquiry;
